<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import AbasRelatorio from './components/AbasRelatorio.vue';
import CriticidadePanel from './CriticidadePanel.vue';
import CruzamentoPanel from './CruzamentoPanel.vue';
import DetratoresPanel from './DetratoresPanel.vue';
import RetabulacaoPanel from './RetabulacaoPanel.vue';

// Farol (R2.4) — a tela que julga a classificação: o que está sendo tabulado, com que gravidade,
// e se a gravidade confere.
//
// ⚠️ T-289 (02/10): ESTE ARQUIVO VIROU UMA CASCA. Até aqui ele era o cabeçalho, as abas E o
// Panorama inteiro (contadores + matriz assunto × sentimento), com filtro, exportação e refresh
// próprios. A Tamara pediu o Panorama fora na reunião de 01/10: "eu não sei até que ponto faz
// sentido a gente ter o panorama e o cruzamento, porque no final do dia eles vão falar da mesma
// coisa. Senão são muitas telas para ter as mesmas informações".
//
// O que foi para onde:
//   · os CINCO CONTADORES foram para dentro do Cruzamento, como bloco titulado "Panorama"
//     ("esses grandes números aqui irem lá para o cruzamento" / "e eu posso chamar de panorama lá
//     dentro"). Vivem em `V2::Reports::FandataContadores`, na mesma resposta da tabela, então
//     passam a respeitar o período, o canal e o lado que a tela mostra, que antes eram dois
//     filtros independentes podendo discordar.
//   · a MATRIZ assunto × sentimento não foi para lugar nenhum. É a duplicata que ela apontou: o
//     Cruzamento cruza causa raiz × protocolo × sentimento, com uma dimensão a mais, e causa raiz
//     é o que a operação CONFIRMOU no encerramento, enquanto assunto é o que a IA achou. Para
//     julgar classificação, o eixo que vale é o confirmado.
//   · o drill-down da matriz não se perdeu: clicar na linha ou na célula do Cruzamento abre a
//     cortina com as conversas, que é o mesmo padrão das outras telas de relatório.
//
// A ABA SE CHAMA CRUZAMENTO, e não Panorama. A fala da Tamara é "chamar de panorama lá dentro",
// que é o bloco; e foi ela quem defendeu a tela justamente pelo que este nome descreve ("porque
// eu cruzo protocolo, sentimento e causa raiz"). Uma aba chamada Panorama que não é um panorama
// recriaria a confusão que o pedido dela tirou.
//
// O endpoint `fandata/farol` continua de pé, só sem consumidor na tela. O motivo está no
// construtor.
const { t } = useI18n();
const route = useRoute();

const ABA_CRITICA = 'critica';
const ABA_CRUZAMENTO = 'cruzamento';
const ABA_DETRATORES = 'detratores';
const ABA_RETABULACAO = 'retabulacao';

// T-203 e T-204 (28/09): a "Fila crítica" SAIU e o CRUZAMENTO tomou o lugar dela.
//
// A aba antiga listava as conversas graves de agora, e isso passou a ser trabalho da fila de
// Conversas, que desde o T-202 vem ordenada pela matriz do clube. Duas telas respondendo a mesma
// pergunta em ordens diferentes era o que fazia as duas discordarem.
//
// O painel antigo, o construtor e a rota continuam de pé, só não são alcançáveis pela navegação.
// Nada foi apagado: a limpeza é na virada, e voltar atrás custa uma linha.
const MOSTRAR_FILA_CRITICA = false;

const abas = computed(() =>
  [
    { key: ABA_CRUZAMENTO, label: t('FAROL.ABAS.CRUZAMENTO') },
    // R7.4: os detratores que chegam pelas redes. Depois do Cruzamento porque é o recorte mais
    // estreito: uma fatia de um canal, enquanto o Cruzamento olha a operação inteira.
    { key: ABA_DETRATORES, label: t('FAROL.ABAS.DETRATORES') },
    // T-268: qualidade da tabulação. Fica no Farol, e não em entrada nova do menu, porque é a
    // mesma família de perguntas das outras abas (o que está sendo classificado e como), e o
    // menu de relatórios foi encolhido de propósito no T-151.
    { key: ABA_RETABULACAO, label: t('FAROL.ABAS.RETABULACAO') },
    MOSTRAR_FILA_CRITICA
      ? { key: ABA_CRITICA, label: t('FAROL.ABAS.CRITICA') }
      : null,
  ].filter(Boolean)
);

// `?aba=panorama` continua chegando: está em link antigo e nos atalhos do relatório de entregas
// que o clube tem na mão. Ele cai no Cruzamento, que é onde o Panorama passou a morar, então o
// link velho leva ao lugar certo em vez de morrer.
const ABAS_POR_LINK = [ABA_DETRATORES, ABA_RETABULACAO];
const abaAtiva = ref(
  ABAS_POR_LINK.includes(route.query.aba) ? route.query.aba : ABA_CRUZAMENTO
);
</script>

<template>
  <div class="flex flex-col w-full h-full px-6 py-4 overflow-auto">
    <div class="flex flex-col gap-3 mb-4">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('FAROL.HEADER') }}
      </h1>
    </div>

    <AbasRelatorio v-if="abas.length > 1" v-model="abaAtiva" :abas="abas" />

    <CriticidadePanel v-if="abaAtiva === ABA_CRITICA" embutido />
    <CruzamentoPanel v-else-if="abaAtiva === ABA_CRUZAMENTO" />
    <DetratoresPanel v-else-if="abaAtiva === ABA_DETRATORES" />
    <RetabulacaoPanel v-else-if="abaAtiva === ABA_RETABULACAO" />
  </div>
</template>
